/**
 * TalentOS's résumé-analysis education data (talentos_resume_analyses.
 * education_summary) is a free-text array -- one combined "degree, field,
 * institution, year" string per entry, in whatever order the résumé happens
 * to list them, NOT ranked by level (see resume_parsing_prompt.txt's own
 * instruction: "one combined string per degree... e.g. 'Bachelor of Science
 * in Computer Science, MIT, 2019'"). Showing entry [0] verbatim (the
 * previous behavior on the Apply Matching candidate cards) was wrong on two
 * counts: it surfaced whichever degree happened to be listed first in the
 * résumé, not the highest one, and it included institution/year that make a
 * dense list row too long.
 *
 * getHighestDegreeAndField picks the highest-ranked entry by degree-level
 * keyword, then trims it to just "degree + field of study" -- everything
 * before the first comma, matching the LLM's own stated format where
 * institution/year always follow a comma.
 */

const DEGREE_RANK: { pattern: RegExp; rank: number }[] = [
  { pattern: /\b(ph\.?d\.?|doctorate|doctoral)\b/i, rank: 5 },
  { pattern: /\b(master'?s?|m\.?tech|m\.?sc|m\.?b\.?a|m\.?c\.?a|m\.?a\.?|post[\s-]?graduate|pg)\b/i, rank: 4 },
  { pattern: /\b(bachelor'?s?|b\.?tech|b\.?e\.?|b\.?sc|b\.?com|b\.?a\.?|b\.?b\.?a|b\.?c\.?a)\b/i, rank: 3 },
  { pattern: /\b(diploma|associate'?s?)\b/i, rank: 2 },
]

const rankOf = (line: string): number => {
  for (const { pattern, rank } of DEGREE_RANK) {
    if (pattern.test(line)) {
      return rank
    }
  }
  return 1
}

/** Picks the highest-ranked degree from a résumé's education_summary array
 *  and trims it to "degree + field of study" only (institution/year
 *  dropped). Returns null for an empty/missing array. */
export const getHighestDegreeAndField = (
  educationSummary: string[] | null | undefined
): string | null => {
  if (!educationSummary || educationSummary.length === 0) {
    return null
  }
  let best = educationSummary[0]
  let bestRank = rankOf(best)
  for (const line of educationSummary.slice(1)) {
    const rank = rankOf(line)
    if (rank > bestRank) {
      best = line
      bestRank = rank
    }
  }
  const commaIndex = best.indexOf(',')
  return (commaIndex === -1 ? best : best.slice(0, commaIndex)).trim()
}
